import { createContext, useCallback, useContext, useRef, useState } from 'react';

export type ToastTone = 'error' | 'info';

/** One thing the person can do about what just happened, offered in the line. */
export interface ToastAction {
  label: string;
  run: () => void;
}

export interface ToastOptions {
  tone?: ToastTone;
  action?: ToastAction;
}

export type ShowToast = (message: string, options?: ToastOptions) => void;

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  action: ToastAction | null;
}

const ToastContext = createContext<ShowToast | null>(null);

const DISMISS_AFTER_MS = 6_000;

export function ToastProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const nextId = useRef(0);

  const show = useCallback<ShowToast>((message, options = {}) => {
    const id = (nextId.current += 1);
    setToasts((current) => [
      ...current,
      { id, tone: options.tone ?? 'error', message, action: options.action ?? null },
    ]);
    window.setTimeout(
      () => setToasts((current) => current.filter((toast) => toast.id !== id)),
      DISMISS_AFTER_MS,
    );
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="toasts">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.tone}`} role="status">
            {toast.message}
            {toast.action !== null && (
              <button
                className="toast-action"
                type="button"
                onClick={() => {
                  setToasts((current) => current.filter((other) => other.id !== toast.id));
                  toast.action?.run();
                }}
              >
                {toast.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ShowToast {
  const show = useContext(ToastContext);
  if (show === null) throw new Error('useToast outside a ToastProvider');
  return show;
}
